import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { I18nContextProvider } from '@ecg-internal/js-react-i18n';
import { ConfiguredToastContainer, Header } from '@hz-design-system/web-ui';

import LoginNudgeApp from '../../apps/LoginNudgeApp/LoginNudgeApp';
import ListingApp from '../../apps/ListingApp/ListingApp';
import DescriptionButtonApp from '../../apps/DescriptionButtonApp/DescriptionButtonApp';
import SellerSidebarApp from '../../apps/SellerSidebarApp/SellerSidebarApp';
import AdmarktSimilarItemsApp from '../../apps/AdmarktSimilarItemsApp/AdmarktSimilarItemsApp';
import ReportApp from '../../apps/ReportApp/ReportApp';
import AdsenseApp from '../../apps/AdsenseApp/AdsenseApp';
import { BANNER_POSITION, bannerPositions } from '../../apps/BannersApp/constants/banners';
import BannersApp from '../../apps/BannersApp/BannersApp';
import InspectionServiceApp from '../../apps/InspectionServiceApp/InspectionServiceApp';
import SimilarAsqApp from '../../apps/SimilarAsqApp/SimilarAsqApp';

import 'react-toastify/dist/ReactToastify.css';

import '../../../scss/global.scss';
import '../../../scss/layout.scss';

// server side only components styles for this page
import '../../../../server/pages/carVip.styles';

function onRecoverableError(error, errInfo) {
  const context = {
    componentStack: undefined,
  };

  if (errInfo?.componentStack) {
    const errorBoundaryError = new Error(error.message);
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
    errorBoundaryError.stack = errInfo.componentStack;

    error.cause = errorBoundaryError;

    context.componentStack = errInfo.componentStack;
  }

  if (window.DD_RUM?.addError) {
    window.DD_RUM.addError(error, { context });
  }
}

export default (i18n) => {
  const appState = window.__CONFIG__ || {};
  const environment = window.__ENV_CONFIG__ || {};
  const headerConfig = window.__HEADER_CONFIG__ || {};
  const {
    listing: {
      flags: {
        showBannersOnVip,
        showExternalAds,
        showSellerOtherItems,
        requestFeeds,
        withBannersFromSeller,
        isLeaseCar,
        hasDeliveryPackages,
        isNoCommercialContent,
      },
      carDetails,
      automotiveTrustIndicators,
    },
  } = appState;
  const {
    experiments: { isHydrateErrorLoggingEnabled },
  } = environment;

  const { inspectionServiceEnabled } = environment;
  const smartBannerAndroidRoot = document.getElementById('smart-banner-android-root');
  const headerRootContainer = document.getElementById('header-root');
  const loginNudgeRoot = document.getElementById('login-nudge-root');
  const listingRoot = document.getElementById('listing-root');
  const descriptionSellerWebsiteLinkRoot = document.getElementById('description-seller-website-link-root');
  const descriptionButtonRoot = document.getElementById('description-button-root');
  const sellerSidebarRoot = document.getElementById('seller-sidebar-root');
  const similarItemsRoot = document.getElementById('similar-items-root');
  const reportRoot = document.getElementById('report-root');
  const sellerOtherItemsRoot = document.getElementById('seller-other-items-root');
  const adsenseRootContainer = document.getElementById('adsense-root');
  const sellerInfoExpandedRoot = document.getElementById('seller-info-expanded-root');
  const servicePackagesRoot = document.getElementById('service-packages-root');
  const sellerLocationRootContainer = document.getElementById('seller-location-root');
  const franchiseDealersItemsRoot = document.getElementById('franchise-dealers-items-root');
  const carValueCheckerRoot = document.getElementById('car-value-checker-root');

  const toastNotificationRoot = document.createElement('div');
  document.body.appendChild(toastNotificationRoot);
  createRoot(toastNotificationRoot).render(<ConfiguredToastContainer />);

  // Render smart banner for Android
  // in case when this is not an Android there is no element to hydrate
  if (environment?.hasSmartBannerAndroidEnabled) {
    const loadComponent = async () => {
      try {
        const SmartBannerAndroidApp = (
          await import(
            /* webpackChunkName: "SmartBannerAndroidApp" */ '../../apps/SmartBannerAndroidApp/SmartBannerAndroidApp'
          )
        ).default;
        hydrateRoot(smartBannerAndroidRoot!, <SmartBannerAndroidApp />);
      } catch {}
    };
    loadComponent();
  }

  // Header is server side rendered in express-react library
  hydrateRoot(headerRootContainer!, <Header headerConfig={headerConfig} />, {
    onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {},
  });

  hydrateRoot(
    loginNudgeRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <LoginNudgeApp environment={environment} />
    </I18nContextProvider>,
    { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
  );

  hydrateRoot(
    listingRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <ListingApp appState={appState} environment={environment} />
    </I18nContextProvider>,
    { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
  );

  import('../../apps/DescriptionSellerWebsiteApp/DescriptionSellerWebsiteLinkApp').then(
    ({ default: DescriptionSellerWebsiteLinkApp }) =>
      hydrateRoot(
        descriptionSellerWebsiteLinkRoot!,
        <I18nContextProvider i18nInstance={i18n}>
          <DescriptionSellerWebsiteLinkApp appState={appState} environment={environment} />
        </I18nContextProvider>,
        { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
      ),
  );

  hydrateRoot(
    descriptionButtonRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <DescriptionButtonApp />
    </I18nContextProvider>,
    { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
  );

  hydrateRoot(
    sellerSidebarRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <SellerSidebarApp appState={appState} environment={environment} showBannersOnVip={showBannersOnVip} />
    </I18nContextProvider>,
    { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
  );

  hydrateRoot(
    similarItemsRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <AdmarktSimilarItemsApp appState={appState} environment={environment} />
    </I18nContextProvider>,
    { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
  );

  hydrateRoot(
    reportRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <ReportApp appState={appState} environment={environment} />
    </I18nContextProvider>,
    { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
  );

  if (sellerInfoExpandedRoot) {
    import('../../apps/SellerInfoExpandedApp/SellerInfoExpandedApp').then(({ default: SellerInfoExpandedApp }) =>
      hydrateRoot(
        sellerInfoExpandedRoot,
        <I18nContextProvider i18nInstance={i18n}>
          <SellerInfoExpandedApp appState={appState} environment={environment} />
        </I18nContextProvider>,
        { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
      ),
    );
  }

  if (!isNoCommercialContent) {
    import('../../apps/FranchiseDealersItemsApp/FranchiseDealersItemsApp').then(
      ({ default: FranchiseDealersItemsApp }) =>
        hydrateRoot(
          franchiseDealersItemsRoot!,
          <I18nContextProvider i18nInstance={i18n}>
            <FranchiseDealersItemsApp appState={appState} environment={environment} />
          </I18nContextProvider>,
          { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
        ),
    );
  }

  if (showSellerOtherItems) {
    import('../../apps/SellerOtherItemsApp/SellerOtherItemsApp').then(({ default: SellerOtherItemsApp }) =>
      hydrateRoot(
        sellerOtherItemsRoot!,
        <I18nContextProvider i18nInstance={i18n}>
          <SellerOtherItemsApp appState={appState} environment={environment} />
        </I18nContextProvider>,
        { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
      ),
    );
  }

  if (hasDeliveryPackages && servicePackagesRoot) {
    import('../../apps/ServicePackagesApp/PackagesApp').then(({ default: PackagesApp }) =>
      hydrateRoot(
        servicePackagesRoot,
        <I18nContextProvider i18nInstance={i18n}>
          <PackagesApp environment={environment} appState={appState} />
        </I18nContextProvider>,
        { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
      ),
    );
  }

  import('../../apps/SellerLocationApp/SellerLocationSectionApp').then(({ default: SellerLocationSectionApp }) =>
    hydrateRoot(
      sellerLocationRootContainer!,
      <I18nContextProvider i18nInstance={i18n}>
        <SellerLocationSectionApp environment={environment} appState={appState} />
      </I18nContextProvider>,
      { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
    ),
  );

  if (showExternalAds) {
    const adsenseRoot = createRoot(adsenseRootContainer!);
    adsenseRoot.render(
      <I18nContextProvider i18nInstance={i18n}>
        <AdsenseApp appState={appState} environment={environment} />
      </I18nContextProvider>,
    );
  }

  // Render banners
  // in case when banners are disabled there is no element to hydrate
  if (showBannersOnVip) {
    Object.values(BANNER_POSITION.STATIC).forEach((position) => {
      const bannersRootContainer = document.getElementById(bannerPositions[position].containerId);
      hydrateRoot(
        bannersRootContainer!,
        <I18nContextProvider i18nInstance={i18n}>
          <BannersApp position={position} environment={environment} />
        </I18nContextProvider>,
        { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
      );
    });
  }

  if (withBannersFromSeller) {
    const bannersRootContainer = document.getElementById(
      bannerPositions[BANNER_POSITION.DYNAMIC.DEALER_TOP].containerId,
    );
    hydrateRoot(
      bannersRootContainer!,
      <I18nContextProvider i18nInstance={i18n}>
        <BannersApp position={BANNER_POSITION.DYNAMIC.DEALER_TOP} environment={environment} />
      </I18nContextProvider>,
      { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
    );
  }

  if (inspectionServiceEnabled && !isLeaseCar && carDetails) {
    const inspectionServiceRoot = document.getElementById('inspection-service-root');
    hydrateRoot(
      inspectionServiceRoot!,
      <I18nContextProvider i18nInstance={i18n}>
        <InspectionServiceApp environment={environment} />
      </I18nContextProvider>,
      { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
    );
  }

  if (automotiveTrustIndicators) {
    const loadComponent = async () => {
      try {
        const AutomotiveTrustIndicatorsApp = (
          await import(
            /* webpackChunkName: "CarTrustIndicatorsApp" */ '../../apps/AutomotiveTrustIndicatorsApp/AutomotiveTrustIndicatorsApp'
          )
        ).default;
        const carTrustIndicatorsRoot = document.getElementById('car-trust-indicators-root');
        hydrateRoot(
          carTrustIndicatorsRoot!,
          <I18nContextProvider i18nInstance={i18n}>
            <AutomotiveTrustIndicatorsApp appState={appState} environment={environment} />
          </I18nContextProvider>,
          { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
        );
      } catch {}
    };
    loadComponent();
  }

  if (carValueCheckerRoot) {
    import(/* webpackChunkName: "CarValueCheckerApp" */ '../../apps/CarValueCheckerApp/CarValueCheckerApp').then(
      ({ default: CarValueCheckerApp }) =>
        hydrateRoot(
          carValueCheckerRoot,
          <I18nContextProvider i18nInstance={i18n}>
            <CarValueCheckerApp appState={appState} environment={environment} />
          </I18nContextProvider>,
          { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
        ),
    );
  }

  if (carDetails) {
    const loadComponent = async () => {
      try {
        const CarAttributesApp = (
          await import(/* webpackChunkName: "CarAttributesApp" */ '../../apps/CarAttributes/CarAttributesApp')
        ).default;
        const carAttributesAccordionRoot = document.getElementById('car-attributes-root');
        hydrateRoot(
          carAttributesAccordionRoot!,
          <I18nContextProvider i18nInstance={i18n}>
            <CarAttributesApp appState={appState} environment={environment} />
          </I18nContextProvider>,
          { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
        );
      } catch {}
    };
    loadComponent();
  }

  if (requestFeeds) {
    const loadComponent = async () => {
      try {
        const FeedApp = (await import(/* webpackChunkName: "FeedApp" */ '../../apps/FeedApp/FeedApp')).default;
        const feedRoot = document.getElementById('feed-root');
        hydrateRoot(
          feedRoot!,
          <I18nContextProvider i18nInstance={i18n}>
            <FeedApp appState={appState} environment={environment} />
          </I18nContextProvider>,
          { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
        );
      } catch {}
    };
    loadComponent();
  }

  const similarAsqRoot = document.getElementById('similar-asq-root');
  if (similarAsqRoot) {
    hydrateRoot(
      similarAsqRoot,
      <I18nContextProvider i18nInstance={i18n}>
        <SimilarAsqApp appState={appState} environment={environment} />
      </I18nContextProvider>,
      { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} },
    );
  }
};
